// Hand-authored catalog for the header global search.
//
// Each entry maps a page to:
//   - name    : display name (kept in sync with menu.constants.ts)
//   - module  : permission module key (parent.state for sub pages, state for links)
//   - page    : permission page key   (child.state for sub pages, state for links)
//   - path    : navigation target (child.routerLink when present, else /module/page)
//   - keywords: static page content (column names, field labels, synonyms)
//
// `module`/`page` MUST match menu.constants.ts so GlobalSearchService can cross-check
// visibility against MenuService.getAll() (already permission-filtered).
//
// To make a page findable by a column/field label, add that label to `keywords`.
export interface SearchIndexEntry {
  name: string;
  module: string;
  page: string;
  path: string;
  keywords: string[];
}

export const SEARCH_INDEX: SearchIndexEntry[] = [
  // 1. Dashboard
  {
    name: 'Dashboard',
    module: 'general-dashboard',
    page: 'general-dashboard',
    path: '/general-dashboard',
    keywords: ['home', 'overview', 'stats', 'statistics', 'charts', 'kpi'],
  },

  // 2. POS
  {
    name: 'POS',
    module: 'pos',
    page: 'pos',
    path: '/pos',
    keywords: ['point of sale', 'cart', 'checkout', 'sell', 'cashier', 'products', 'payment'],
  },

  // 3. Invoices
  {
    name: 'Invoices',
    module: 'invoices',
    page: 'invoices',
    path: '/invoices',
    keywords: [
      'invoice number', 'invoice date', 'status', 'subtotal', 'total', 'type',
      'member id', 'client name', 'location', 'payment method', 'billing', 'receipt', 'tax',
    ],
  },

  // 4. Client Management
  {
    name: 'All Clients',
    module: 'client-management',
    page: 'all-clients',
    path: '/client-management/all-clients',
    keywords: ['clients', 'members', 'name', 'phone', 'email', 'member id', 'gender', 'status', 'customers'],
  },
  {
    name: 'Unsubscribed Clients',
    module: 'client-management',
    page: 'unsubscribed-clients',
    path: '/client-management/unsubscribed-clients',
    keywords: ['expired', 'inactive members', 'no membership', 'lapsed'],
  },
  {
    name: 'Subscribed Clients',
    module: 'client-management',
    page: 'subscribed-clients',
    path: '/client-management/subscribed-clients',
    keywords: ['active members', 'active clients', 'membership', 'subscribers'],
  },
  {
    name: 'Verify Clients',
    module: 'client-management',
    page: 'verify-clients',
    path: '/client-management/verify-clients',
    keywords: ['verification', 'pending verification', 'approve clients'],
  },
  {
    name: 'Guests Requests',
    module: 'client-management',
    page: 'guest-requests',
    path: '/guest-requests',
    keywords: ['guests', 'visitor requests', 'day pass'],
  },
  {
    name: 'Membership Requests',
    module: 'client-management',
    page: 'membership-requests',
    path: '/membership-requests',
    keywords: ['membership', 'requests', 'pending memberships', 'approvals'],
  },
  {
    name: 'Subscription List',
    module: 'client-management',
    page: 'subscription',
    path: '/subscription',
    keywords: ['subscriptions', 'plans', 'membership list'],
  },
  {
    name: 'Consultation Form',
    module: 'client-management',
    page: 'consultation-form',
    path: '/gym-management/assessments',
    keywords: ['assessment', 'consultation', 'health form', 'intake'],
  },
  {
    name: 'Pending Consultation Forms',
    module: 'client-management',
    page: 'pending-assessments',
    path: '/pending-assessments',
    keywords: ['pending assessments', 'consultation', 'to review'],
  },

  // 5. F&B
  {
    name: 'Foodics Configuration',
    module: 'fnb',
    page: 'foodics',
    path: '/foodics',
    keywords: ['foodics', 'integration', 'configuration', 'f&b', 'food beverage'],
  },
  {
    name: 'Foodics Offline Orders',
    module: 'fnb',
    page: 'integration-orders',
    path: '/integration-orders',
    keywords: ['foodics', 'offline orders', 'integration orders', 'f&b'],
  },

  // 6. Club Management
  {
    name: 'Schedule',
    module: 'gym-management',
    page: 'schedule',
    path: '/gym-management/schedule',
    keywords: ['timetable', 'classes', 'sessions schedule', 'calendar', 'instructor'],
  },
  {
    name: 'Sessions',
    module: 'gym-management',
    page: 'sessions',
    path: '/gym-management/sessions',
    keywords: ['session list', 'training sessions', 'classes'],
  },
  {
    name: 'Session Reviews',
    module: 'gym-management',
    page: 'sessions-review',
    path: '/gym-management/sessions-review',
    keywords: ['reviews', 'feedback', 'ratings', 'session review'],
  },
  {
    name: 'Reservations Calendar',
    module: 'gym-management',
    page: 'reservations-calendar',
    path: '/reservations-calendar',
    keywords: ['reservations', 'bookings', 'calendar'],
  },
  {
    name: 'Fitness Calendar',
    module: 'gym-management',
    page: 'fitness-calendar',
    path: '/fitness-calendar',
    keywords: ['fitness', 'calendar', 'bookings'],
  },
  {
    name: 'Wellness Calendar',
    module: 'gym-management',
    page: 'wellness-calendar',
    path: '/wellness-calendar',
    keywords: ['wellness', 'calendar', 'spa', 'recovery'],
  },
  {
    name: 'Nutrition Calendar',
    module: 'gym-management',
    page: 'nutrition-calendar',
    path: '/nutrition-calendar',
    keywords: ['nutrition', 'calendar', 'diet appointments'],
  },
  {
    name: 'Group Classes Calendar',
    module: 'gym-management',
    page: 'group-classes-calendar',
    path: '/group-classes-calendar',
    keywords: ['group classes', 'calendar', 'classes'],
  },
  {
    name: 'Calendar View',
    module: 'gym-management',
    page: 'general-calendar-view',
    path: '/general-calendar-view',
    keywords: ['calendar', 'general view', 'agenda'],
  },
  {
    name: "Today's Sessions",
    module: 'gym-management',
    page: 'today-sessions',
    path: '/today-sessions',
    keywords: ['today', 'sessions', 'daily sessions'],
  },

  // 7. Merchandise Management
  {
    name: 'Merchandise',
    module: 'food-beverage',
    page: 'stock',
    path: '/food-beverage/stock',
    keywords: ['stock', 'inventory', 'products', 'items', 'merchandise'],
  },
  {
    name: 'Orders Management',
    module: 'food-beverage',
    page: 'orders',
    path: '/food-beverage/orders',
    keywords: ['orders', 'purchase', 'sales orders'],
  },

  // 8. Nutrition Management
  {
    name: 'Nutrition Elements',
    module: 'nutrition-management',
    page: 'nutrition-elements',
    path: '/nutrition-management/nutrition-elements',
    keywords: ['food items', 'ingredients', 'meals', 'nutrition'],
  },
  {
    name: 'Nutrition Plans',
    module: 'nutrition-management',
    page: 'nutrition-plans',
    path: '/nutrition-management/nutrition-plans',
    keywords: ['diet plans', 'meal plans', 'nutrition'],
  },
  {
    name: 'Assigned Clients',
    module: 'nutrition-management',
    page: 'assigned-clients',
    path: '/nutrition-management/assigned-clients',
    keywords: ['assigned', 'nutritionist clients', 'my clients'],
  },

  // 9. Staff Management
  {
    name: 'Staff Instructors',
    module: 'personnel-management',
    page: 'trainers',
    path: '/personnel-management/trainers',
    keywords: ['trainers', 'instructors', 'coaches', 'staff', 'name', 'email', 'role'],
  },
  {
    name: 'Staff Instructor Roles',
    module: 'personnel-management',
    page: 'trainer-roles',
    path: '/personnel-management/trainer-roles',
    keywords: ['trainer roles', 'instructor roles', 'permissions'],
  },
  {
    name: 'Staff Users',
    module: 'personnel-management',
    page: 'users',
    path: '/personnel-management/users',
    keywords: ['users', 'accounts', 'employees', 'staff', 'name', 'email', 'role'],
  },
  {
    name: 'Staff Roles',
    module: 'personnel-management',
    page: 'roles',
    path: '/personnel-management/roles',
    keywords: ['roles', 'permissions', 'access control'],
  },

  // 10. Settings
  {
    name: 'Wallet Configuration',
    module: 'settings',
    page: 'wallet-configurations',
    path: '/wallet-configurations',
    keywords: ['wallet', 'credit', 'balance settings'],
  },
  {
    name: 'Injury Freeze Reasons',
    module: 'settings',
    page: 'reasons',
    path: '/reasons',
    keywords: ['freeze reasons', 'injury', 'pause membership', 'reasons'],
  },
  {
    name: 'Installments',
    module: 'settings',
    page: 'installments',
    path: '/installments',
    keywords: ['installments', 'payment plans', 'instalment'],
  },
  {
    name: 'Terms and Conditions',
    module: 'settings',
    page: 'terms-conditions',
    path: '/terms-conditions',
    keywords: ['terms', 'conditions', 'policy', 't&c'],
  },
  {
    name: 'Agreement',
    module: 'settings',
    page: 'agreement',
    path: '/agreement',
    keywords: ['agreement', 'contract', 'waiver'],
  },
  {
    name: 'Division',
    module: 'settings',
    page: 'superCategories',
    path: '/gym-management/superCategories',
    keywords: ['division', 'super categories', 'grouping'],
  },
  {
    name: 'Type',
    module: 'settings',
    page: 'categories',
    path: '/gym-management/categories',
    keywords: ['type', 'categories', 'classification'],
  },
  {
    name: 'Machines',
    module: 'settings',
    page: 'machines',
    path: '/gym-management/machines',
    keywords: ['machines', 'equipment', 'devices'],
  },
  {
    name: 'Locations',
    module: 'settings',
    page: 'locations',
    path: '/gym-management/locations',
    keywords: ['locations', 'branches', 'clubs', 'sites'],
  },
  {
    name: 'Memberships',
    module: 'settings',
    page: 'memberships',
    path: '/gym-management/memberships',
    keywords: ['memberships', 'plans', 'packages', 'price', 'duration'],
  },
  {
    name: 'Challenges',
    module: 'settings',
    page: 'challenges',
    path: '/gym-management/challenges',
    keywords: ['challenges', 'competitions', 'programs'],
  },
  {
    name: 'Events',
    module: 'settings',
    page: 'events',
    path: '/gym-management/events',
    keywords: ['events', 'activities', 'workshops'],
  },
  {
    name: 'Complementary',
    module: 'settings',
    page: 'complementary',
    path: '/complementary',
    keywords: ['complementary', 'free', 'gifts', 'add-ons'],
  },

  // 11. Communication Management
  {
    name: 'Newsletter',
    module: 'communication',
    page: 'newsletter',
    path: '/client-management/newsletter',
    keywords: ['newsletter', 'email campaign', 'broadcast'],
  },
  {
    name: 'Push Notifications',
    module: 'communication',
    page: 'push-notification',
    path: '/push-notification',
    keywords: ['push', 'notifications', 'alerts', 'messages'],
  },
  {
    name: 'Notifications',
    module: 'communication',
    page: 'notifications',
    path: '/notifications',
    keywords: ['notifications', 'alerts', 'messages'],
  },

  // 12. Activity Logs
  {
    name: 'Activity Logs',
    module: 'activity-logs',
    page: 'activity-logs',
    path: '/activity-logs',
    keywords: ['logs', 'audit', 'history', 'activity', 'actions'],
  },

  // 13. Reports
  {
    name: 'Sales Management',
    module: 'reports',
    page: 'sales-management',
    path: '/sales-management',
    keywords: ['sales', 'revenue', 'transactions'],
  },
  {
    name: 'Attendance',
    module: 'reports',
    page: 'attendance',
    path: '/reports/attendance',
    keywords: ['attendance', 'check in', 'visits', 'report'],
  },
  {
    name: 'Installments',
    module: 'reports',
    page: 'installment',
    path: '/reports/installment',
    keywords: ['installments', 'due payments', 'report'],
  },
  {
    name: 'Credit',
    module: 'reports',
    page: 'credit',
    path: '/reports/credit',
    keywords: ['credit', 'balance', 'report'],
  },
  {
    name: 'Commission',
    module: 'reports',
    page: 'commission',
    path: '/reports/commission',
    keywords: ['commission', 'trainer earnings', 'report'],
  },
  {
    name: 'Instructor Commission',
    module: 'reports',
    page: 'instructor-commission',
    path: '/reports/instructor-commission',
    keywords: ['instructor commission', 'trainer commission', 'commission by trainer', 'burn amount', 'report'],
  },
  {
    name: 'Walk-ins',
    module: 'reports',
    page: 'walk-ins',
    path: '/reports/walk-ins',
    keywords: ['walk ins', 'visitors', 'report'],
  },
  {
    name: 'New Clients',
    module: 'reports',
    page: 'new-trainees',
    path: '/reports/new-trainees',
    keywords: ['new clients', 'new members', 'new trainees', 'report'],
  },
  {
    name: 'Renewal',
    module: 'reports',
    page: 'renewal',
    path: '/reports/renewal',
    keywords: ['renewal', 'renewals', 'report'],
  },
  {
    name: 'Membership',
    module: 'reports',
    page: 'membership',
    path: '/reports/membership',
    keywords: ['membership', 'report'],
  },
  {
    name: 'Membership Credit',
    module: 'reports',
    page: 'membership-credit',
    path: '/reports/membership-credit',
    keywords: ['membership credit', 'report'],
  },
  {
    name: 'Active Membership',
    module: 'reports',
    page: 'active-membership-report',
    path: '/reports/active-membership-report',
    keywords: ['active membership', 'report'],
  },
  {
    name: 'Sales Report',
    module: 'reports',
    page: 'sales-report',
    path: '/reports/sales-report',
    keywords: ['sales', 'revenue', 'report'],
  },
  {
    name: 'Membership Renewal',
    module: 'reports',
    page: 'membership-renewal',
    path: '/reports/membership-renewal',
    keywords: ['membership renewal', 'report'],
  },
  {
    name: 'Challenge Credit',
    module: 'reports',
    page: 'challenge-credit',
    path: '/reports/challenge-credit',
    keywords: ['challenge credit', 'report'],
  },
  {
    name: 'Collective Report',
    module: 'reports',
    page: 'session-credit',
    path: '/reports/session-credit',
    keywords: ['collective', 'session credit', 'report'],
  },
  {
    name: 'Reject Report',
    module: 'reports',
    page: 'reject-report',
    path: '/reports/reject-report',
    keywords: ['reject', 'rejected', 'report'],
  },
  {
    name: 'Retail Sales Report',
    module: 'reports',
    page: 'retail-sales-report',
    path: '/reports/retail-sales-report',
    keywords: ['retail sales', 'merchandise sales', 'report'],
  },
  {
    name: 'Membership Lifecycle Report',
    module: 'reports',
    page: 'membership-lifecycle-report',
    path: '/reports/membership-lifecycle-report',
    keywords: ['membership lifecycle', 'report'],
  },

  // Trainer-only reports (surface only for trainer/nutritionist roles)
  {
    name: 'Instructor Commission',
    module: 'reports',
    page: 'trainer-commission',
    path: '/reports/trainer-commission',
    keywords: ['instructor commission', 'trainer commission', 'earnings', 'report'],
  },
  {
    name: 'Instructor Active Client',
    module: 'reports',
    page: 'trainer-active-trainee',
    path: '/reports/trainer-active-trainee',
    keywords: ['instructor active client', 'active trainees', 'report'],
  },
  {
    name: 'Services',
    module: 'reports',
    page: 'services-report',
    path: '/reports/services-report',
    keywords: ['services', 'report'],
  },
];
